import React, { useState, useEffect } from 'react';
import { Mail, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { useNavigate, Link } from 'react-router-dom';
import carImage from '../../assets/images/car.png'

// Funções de criptografia e descriptografia
const encryptData = (text: string): string => {
  return btoa(text.split('').map((char) =>
    String.fromCharCode(char.charCodeAt(0) + 3)
  ).join(''));
};

const decryptData = (encryptedText: string): string => {
  try {
    return atob(encryptedText).split('').map((char) =>
      String.fromCharCode(char.charCodeAt(0) - 3)
    ).join('');
  } catch {
    return '';
  }
};

export function Login() {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [salvarAcesso, setSalvarAcesso] = useState(false);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();

  // Efeito para verificar se existem credenciais salvas
  useEffect(() => {
    const emailSalvo = localStorage.getItem('emailUsuario');
    const senhaSalva = localStorage.getItem('senhaUsuario');

    if (emailSalvo && senhaSalva) {
      setEmail(decryptData(emailSalvo));
      setSenha(decryptData(senhaSalva));
      setSalvarAcesso(true);
    }
  }, []);

  // Efeito para controlar o redirecionamento após o login
  useEffect(() => {
    if (user) {
      // Se o usuário for admin ou revenda, redireciona para a página inicial
      if (user.tipo === 'admin' || user.tipo === 'revenda') {
        navigate('/home');
      } else {
        // Se o usuário não for admin ou revenda, redireciona para a página de impressão de chassi
        navigate('/imprimir-chassi');
      }
    }
  }, [user, navigate]);

  // Função para lidar com o envio do formulário de login
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); // Limpa erros anteriores
    setLoading(true); // Inicia o carregamento

    try {
      // Tenta fazer o login com o email e senha fornecidos
      await signIn(email, senha);

      // Se a opção "Salvar Acesso" estiver marcada, salva as credenciais
      if (salvarAcesso) {
        localStorage.setItem('emailUsuario', encryptData(email));
        localStorage.setItem('senhaUsuario', encryptData(senha));
      } else {
        // Se não estiver marcada, remove qualquer credencial salva
        localStorage.removeItem('emailUsuario');
        localStorage.removeItem('senhaUsuario');
      }
    } catch (error: any) {
      // Se ocorrer um erro, exibe uma mensagem de erro
      setError(error.message || 'Erro ao fazer login');
    } finally {
      // Sempre desativa o estado de carregamento ao finalizar
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4 relative">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">

        <div className="text-center mb-8">
          <img src={carImage} alt="Automóvel" className="mx-auto mb-4" />
          <h1 className="text-1xl font-bold text-gray-800 mb-2">Bem-vindo ao Sistema de Gravação</h1>
          <p className="text-gray-600">Faça login para continuar</p>
        </div>

        {/* Aviso de instabilidade — desativado.
            Para reativar, reimportar AlertTriangle de 'lucide-react' no topo do arquivo. */}
        {/* <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6 rounded-md shadow-sm">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
            <p className="text-sm text-yellow-800 leading-relaxed">
              <strong>Atenção!</strong> Sistemas de armazenamento globais com instabilidades! Caso esteja com problemas de conexão, contate o suporte!
            </p>
          </div>
        </div> */}

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Seu e-mail"
            icon={<Mail className="w-5 h-5" />}
            required
          />

          <Input
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            placeholder="Sua senha"
            icon={<Lock className="w-5 h-5" />}
            required
          />

          <div className="flex items-center">
            <input
              type="checkbox"
              id="salvarAcesso"
              checked={salvarAcesso}
              onChange={(e) => setSalvarAcesso(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="salvarAcesso" className="ml-2 block text-sm text-gray-700">
              Salvar Acesso
            </label>
          </div>

          <Button
            type="submit"
            disabled={loading}
            isLoading={loading}
            className="w-full"
          >
            Entrar
          </Button>

          <Link to="/esqueceu-senha" className="block w-full text-sm text-blue-600 hover:text-blue-500 text-center">
            Esqueceu a senha?
          </Link>
        </form>

        {/* Não há autocadastro: novos usuários são criados apenas por admin ou
            revenda, em /usuarios/novo (Edge Function criar-usuario). */}
      </div>

      {/* Versao do sistema */}
      <div className="absolute bottom-4 text-center w-full">
        <p className="text-white text-xs opacity-70">v{__APP_VERSION__}</p>
      </div>
    </div>
  );
}